import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { authService, MFASetupData, AuthUser } from '../services/auth';

interface MFASetupProps {
  mfaData: MFASetupData;
  cognitoUser: CognitoUser;
  onSuccess: (user: AuthUser) => void;
  onCancel: () => void;
}

export const MFASetup: React.FC<MFASetupProps> = ({
  mfaData,
  cognitoUser,
  onSuccess,
  onCancel
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const steps = ['Download App', 'Scan QR Code', 'Verify Code'];

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const user = await authService.verifyMFASetup(cognitoUser, verificationCode);
      onSuccess(user);
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please check your code and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        p: 2
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%'
        }}
      >
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 3 }}>
          Set Up Two-Factor Authentication
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Alert severity="info" sx={{ mb: 3 }}>
          Two-factor authentication adds an extra layer of security to your account.
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Step 1: Download Authenticator App
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Download one of these apps on your smartphone:
          </Typography>
          <ul>
            <li><Typography variant="body2">Google Authenticator (iOS/Android)</Typography></li>
            <li><Typography variant="body2">Microsoft Authenticator (iOS/Android)</Typography></li>
            <li><Typography variant="body2">Authy (iOS/Android)</Typography></li>
          </ul>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setActiveStep(1)}
            sx={{ mt: 1 }}
          >
            I have the app
          </Button>
        </Box>

        {activeStep >= 1 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Step 2: Scan QR Code
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Open your authenticator app and scan this QR code:
            </Typography>
            
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                p: 2,
                bgcolor: 'white',
                borderRadius: 1,
                border: '1px solid #ddd'
              }}
            >
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(mfaData.qrCodeUrl)}`}
                alt="QR Code for MFA Setup"
                style={{ width: 200, height: 200 }}
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }} align="center">
              Can't scan? Enter this code manually: <strong>{mfaData.secretCode}</strong>
            </Typography>

            <Button
              variant="outlined"
              size="small"
              onClick={() => setActiveStep(2)}
              sx={{ mt: 2 }}
              fullWidth
            >
              I've scanned the code
            </Button>
          </Box>
        )}

        {activeStep >= 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 3: Enter Verification Code
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Enter the 6-digit code from your authenticator app:
            </Typography>

            <TextField
              fullWidth
              label="6-Digit Code"
              value={verificationCode}
              onChange={handleCodeChange}
              placeholder="000000"
              inputProps={{
                maxLength: 6,
                style: { fontSize: '24px', textAlign: 'center', letterSpacing: '8px' }
              }}
              sx={{ mb: 2 }}
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter' && verificationCode.length === 6) {
                  handleVerify();
                }
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={onCancel}
                fullWidth
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleVerify}
                fullWidth
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify & Complete Setup'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

